import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MinimizedMeetingService, MinimizedMeetingData } from '../services/minimized-meeting.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { MeetingSummaryDialogComponent } from '../meeting-summary-dialog/meeting-summary-dialog.component';

@Component({
  selector: 'app-minimized-meeting-indicator',
  templateUrl: './minimized-meeting-indicator.component.html',
  styleUrls: ['./minimized-meeting-indicator.component.scss']
})
export class MinimizedMeetingIndicatorComponent implements OnInit, OnDestroy {
  minimizedMeetings: MinimizedMeetingData[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private minimizedMeetingService: MinimizedMeetingService,
    private signalRService: SignalRService,
    private userService: UserService
  ) {}

  private normalizeMeetingId(value: any): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private meetingMatches(meeting: MinimizedMeetingData, meetingId: any): boolean {
    const target = this.normalizeMeetingId(meetingId);
    if (!target) {
      return false;
    }

    const candidates = [
      meeting.meetingId,
      meeting.dialogData?.meetingId,
      meeting.dialogData?.meetingIdInt,
      meeting.dialogData?.id
    ];

    return candidates.some(candidate => this.normalizeMeetingId(candidate) === target);
  }

  ngOnInit(): void {
    // Helper function to filter and display meetings
    const updateMinimizedMeetings = () => {
      const meetings = this.minimizedMeetingService.getMinimizedMeetings();
      
      // SIMPLE RULE: If any dialog is open, don't show toaster at all
      let isDialogOpen = false;
      try {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
        isDialogOpen = !!dialogElement;
      } catch (e) {
        isDialogOpen = false;
      }
      
      // If dialog is open, show nothing - period
      if (isDialogOpen) {
        this.minimizedMeetings = [];
        return;
      }
      
      // Filter out meetings that are completed or cancelled
      let filteredMeetings = meetings.filter(meeting => {
        const status = meeting.status?.toString().toLowerCase() || '';
        const isCompleted = status === 'completed' || status === '5';
        const isCancelled = status === 'cancelled' || status === '4';
        return !isCompleted && !isCancelled;
      });
      
      this.minimizedMeetings = filteredMeetings;
    };
    
    // Subscribe to minimized meetings changes
    const sub = this.minimizedMeetingService.minimizedMeetings$.subscribe(() => {
      updateMinimizedMeetings();
    });
    this.subscriptions.push(sub);
    
    // CRITICAL: Also subscribe to router events to update when navigating away from meeting details
    const routerSub = this.router.events.subscribe(() => {
      updateMinimizedMeetings();
    });
    this.subscriptions.push(routerSub);
    
    // Periodically check for dialogs - if dialog opens, remove meetings from display
    const dialogCheckInterval = setInterval(() => {
      try {
        // Check if dialog is open
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
        if (dialogElement) {
          // Dialog is open - clear display (but don't remove from service, they'll come back when dialog closes)
          this.minimizedMeetings = [];
          return;
        }
        // The add event can fire before the dialog finishes closing, so refresh after it closes.
        updateMinimizedMeetings();
      } catch (e) {
        // Ignore errors
      }
    }, 50); // Check every 50ms for faster response
    
    // Store interval to clear it on destroy
    (this as any).dialogCheckInterval = dialogCheckInterval;

    // Listen for meeting status changed events (broadcasted to all users)
    const meetingStatusChangedSub = this.signalRService.meetingStatusChanged$.subscribe(
      (statusData) => {
        console.log('MeetingStatusChanged event received in toaster component:', statusData);
        // Prioritize string MeetingId first, then fallback to integer ID
        // The string MeetingId (e.g., "MEET-2024-001") is the primary identifier
        const meetingIdToUse = statusData.meetingId || statusData.meetingIdInt || statusData.id?.toString();
        if (meetingIdToUse) {
          // Simple check: If dialog is open, don't add toaster
          // For OnGoing status: also check if user is on meeting page (dialog will auto-open)
          // For WaitingForAttendance: show toaster even if on meeting page (dialog doesn't auto-open)
          setTimeout(() => {
            let isDialogOpen = false;
            try {
              const overlayContainer = document.querySelector('.cdk-overlay-container');
              const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
              isDialogOpen = !!dialogElement;
            } catch (e) {
              isDialogOpen = false;
            }
            
            if (isDialogOpen) {
              console.log('Dialog is open - not adding to toaster');
              this.minimizedMeetingService.removeMinimizedMeeting(meetingIdToUse);
              return;
            }
            
            // Check meeting status - only skip if OnGoing and on meeting page
            const status = statusData.status?.toLowerCase() || '';
            const isOnGoing = status === 'ongoing' || status === '3';
            
            // For OnGoing meetings, also check if user is on the meeting page (dialog will auto-open)
            if (isOnGoing) {
              let isOnMeetingPage = false;
              try {
                const currentUrl = window.location.pathname;
                const meetingUrl = `/meetings/view/${meetingIdToUse}`;
                isOnMeetingPage = currentUrl === meetingUrl || currentUrl.includes(`/meetings/view/${meetingIdToUse}`);
              } catch (e) {
                isOnMeetingPage = false;
              }
              
              if (isOnMeetingPage) {
                console.log('OnGoing meeting and on meeting page - dialog will auto-open, not adding to toaster');
                this.minimizedMeetingService.removeMinimizedMeeting(meetingIdToUse);
                return;
              }
            }
            
            // Check if current user is invited to the meeting
            const userEmail = this.userService.user?.EmailAddress?.toLowerCase() || '';
            const members = statusData.members || [];
            const isInvited = members.some((m: any) => {
              const memberEmail = (m.email || m.userEmail || '').toLowerCase();
              return memberEmail && memberEmail === userEmail;
            });
            
            // Add/update toaster if user is invited
            if (isInvited || members.length === 0) {
              const minimizedMeetings = this.minimizedMeetingService.getMinimizedMeetings();
              const existingMinimized = minimizedMeetings.find(m => 
                this.meetingMatches(m, meetingIdToUse) ||
                this.meetingMatches(m, statusData.meetingIdInt) ||
                this.meetingMatches(m, statusData.id)
              );
              
              if (existingMinimized) {
                // Update status
                this.minimizedMeetingService.updateMeetingStatus(existingMinimized.meetingId, statusData.status);
              } else {
                // Add meeting to minimized list for toaster
                const status = statusData.status?.toLowerCase() || '';
                if (status === 'waitingforattendance' || status === '2' || status === 'ongoing' || status === '3') {
                  this.minimizedMeetingService.addMinimizedMeeting({
                    meetingId: meetingIdToUse,
                    meetingTitle: statusData.meetingTitle,
                    status: statusData.status,
                    dialogType: status === 'waitingforattendance' || status === '2' ? 'waiting' : 'join',
                    dialogData: {
                      meetingTitle: statusData.meetingTitle,
                      meetingId: meetingIdToUse,
                      meetingIdInt: statusData.id?.toString() || statusData.meetingIdInt,
                      id: statusData.id,
                      members: members,
                      agendaItems: statusData.agendaItems || []
                    }
                  });
                }
              }
            }
          }, 500); // Longer delay to ensure dialog has time to open and render
        }
      }
    );
    this.subscriptions.push(meetingStatusChangedSub);

    // Listen for meeting session start events to update toaster
    const meetingSessionSub = this.signalRService.meetingSession$.subscribe(
      (sessionData) => {
        console.log('MeetingSession event received in toaster component:', sessionData);
        // Prioritize string MeetingId first, then fallback to integer ID
        const meetingIdToUse = sessionData.meetingId || (sessionData as any).meetingIdInt || (sessionData as any).id?.toString();
        if (meetingIdToUse) {
          // Check if dialog is open, don't add toaster
          // For OnGoing status: also check if user is on meeting page (dialog will auto-open)
          // For WaitingForAttendance: show toaster even if on meeting page (dialog doesn't auto-open)
          setTimeout(() => {
            let isDialogOpen = false;
            try {
              const overlayContainer = document.querySelector('.cdk-overlay-container');
              const dialogElement = overlayContainer?.querySelector('.mat-dialog-container');
              isDialogOpen = !!dialogElement;
            } catch (e) {
              isDialogOpen = false;
            }
            
            if (isDialogOpen) {
              console.log('Dialog is open - not adding to toaster');
              this.minimizedMeetingService.removeMinimizedMeeting(meetingIdToUse);
              return;
            }
            
            // Check meeting status - only skip if OnGoing and on meeting page
            const status = sessionData.meetingData?.status || 'WaitingForAttendance';
            const statusLower = status?.toLowerCase() || '';
            const isOnGoing = statusLower === 'ongoing' || statusLower === '3';
            
            // For OnGoing meetings, also check if user is on the meeting page (dialog will auto-open)
            if (isOnGoing) {
              let isOnMeetingPage = false;
              try {
                const currentUrl = window.location.pathname;
                const meetingUrl = `/meetings/view/${meetingIdToUse}`;
                isOnMeetingPage = currentUrl === meetingUrl || currentUrl.includes(`/meetings/view/${meetingIdToUse}`);
              } catch (e) {
                isOnMeetingPage = false;
              }
              
              if (isOnMeetingPage) {
                console.log('OnGoing meeting and on meeting page - dialog will auto-open, not adding to toaster');
                this.minimizedMeetingService.removeMinimizedMeeting(meetingIdToUse);
                return;
              }
            }
            
            // Check if current user is invited to the meeting
            const userEmail = this.userService.user?.EmailAddress?.toLowerCase() || '';
            const members = sessionData.meetingData?.members || [];
            const isInvited = members.some((m: any) => {
              const memberEmail = (m.email || m.userEmail || '').toLowerCase();
              return memberEmail && memberEmail === userEmail;
            });
            
            // Add/update toaster if user is invited
            if (isInvited || members.length === 0) {
              const minimizedMeetings = this.minimizedMeetingService.getMinimizedMeetings();
              const existingMinimized = minimizedMeetings.find(m => m.meetingId === meetingIdToUse);
              
              if (existingMinimized) {
                // Update status to WaitingForAttendance
                const newStatus = sessionData.meetingData?.status || 'WaitingForAttendance';
                this.minimizedMeetingService.updateMeetingStatus(meetingIdToUse, newStatus);
              } else {
                // Add meeting to minimized list for toaster
                const meetingTitle = sessionData.meetingTitle || 'Meeting';
                const status = sessionData.meetingData?.status || 'WaitingForAttendance';
                this.minimizedMeetingService.addMinimizedMeeting({
                  meetingId: meetingIdToUse,
                  meetingTitle: meetingTitle,
                  status: status,
                  dialogType: 'waiting',
                  dialogData: {
                    meetingTitle: meetingTitle,
                    meetingId: meetingIdToUse,
                    meetingIdInt: (sessionData as any).meetingIdInt || (sessionData as any).id?.toString(),
                    id: (sessionData as any).id,
                    members: members,
                    agendaItems: sessionData.meetingData?.agendaItems || []
                  }
                });
              }
            }
          }, 500); // Longer delay to ensure dialog has time to open and render
        }
      }
    );
    this.subscriptions.push(meetingSessionSub);

    // Listen for meeting ended events to remove toaster immediately
    const meetingEndedSub = this.signalRService.meetingEnded$.subscribe(
      (data) => {
        console.log('MeetingEnded event received in toaster component:', data);
        if (data.meetingId) {
          const minimizedMeeting = this.minimizedMeetingService
            .getMinimizedMeetings()
            .find(meeting => this.meetingMatches(meeting, data.meetingId));

          if (minimizedMeeting?.dialogType === 'join') {
            const dialogData = minimizedMeeting.dialogData || {};
            const agendaItems = dialogData.agendaItems || [];
            const comments = new Map<number, string>();
            (dialogData.agendaComments || []).forEach((entry: any) => {
              comments.set(Number(entry.agendaIndex), entry.comment || '');
            });
            const createResponses = (indices: number[], response: string, responseIcon: string) =>
              (indices || []).map((index: number) => ({
                ...(agendaItems[index] || {}),
                comment: comments.get(Number(index)) || '',
                response,
                responseIcon,
                submittedDate: new Date().toLocaleString()
              }));

            this.dialog.open(MeetingSummaryDialogComponent, {
              width: '1000px',
              maxWidth: '95vw',
              maxHeight: '95vh',
              panelClass: 'meeting-summary-dialog',
              data: {
                isHoster: false,
                acceptedItems: createResponses(dialogData.acceptedAgendaItems, 'Accepted', 'check-circle'),
                rejectedItems: createResponses(dialogData.rejectedAgendaItems, 'Rejected', 'times-circle'),
                deferredItems: createResponses(dialogData.deferredAgendaItems, 'Deferred', 'clock')
              }
            });
          }

          this.minimizedMeetingService.updateMeetingStatus(data.meetingId, 'Completed');
          this.minimizedMeetingService.removeMinimizedMeeting(data.meetingId);
          this.minimizedMeetingService.removeMeetingsByStatus(['Completed', '5', 'completed', 'Cancelled', '4', 'cancelled']);
        }
      }
    );
    this.subscriptions.push(meetingEndedSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Clear dialog check interval if it exists
    if ((this as any).dialogCheckInterval) {
      clearInterval((this as any).dialogCheckInterval);
    }
  }

  // Restore a minimized meeting dialog
  restoreMeeting(meeting: MinimizedMeetingData, event?: Event): void {
    // If event is from a button click, stop propagation but still restore
    if (event) {
      event.stopPropagation();
    }
    
    // Check current route - route is /meetings/view/:id
    const currentUrl = this.router.url;
    const meetingUrl = `/meetings/view/${meeting.meetingId}`;
    
    // Restore directly when the user is already on the meeting page.
    if (currentUrl === meetingUrl || currentUrl.includes(`/meetings/view/${meeting.meetingId}`)) {
      window.dispatchEvent(new CustomEvent('restore-meeting-dialog', { detail: meeting }));
      return;
    }
    
    // Keep the snapshot until the destination page has consumed it.
    this.router.navigate(['/meetings', 'view', meeting.meetingId]);
  }

  // Close a minimized meeting
  closeMeeting(meetingId: string, event: Event): void {
    event.stopPropagation();
    this.minimizedMeetingService.removeMinimizedMeeting(meetingId);
  }

  // Get status display text
  getStatusText(status: string): string {
    if (status === 'WaitingForAttendance' || status === '2') {
      return 'Waiting for Attendance';
    }
    if (status === 'OnGoing' || status === '3') {
      return 'On Going';
    }
    return status;
  }

  // Get status color class
  getStatusClass(status: string): string {
    if (status === 'WaitingForAttendance' || status === '2') {
      return 'status-waiting';
    }
    if (status === 'OnGoing' || status === '3') {
      return 'status-ongoing';
    }
    return 'status-default';
  }
}

